/**
 * Reading a v3 error (WBS 7.0 / 7.10).
 *
 * The server now answers every failure with one body — `{ data: { error_code,
 * message?, validations? } }` — whichever layer raised it. Before that there were
 * four shapes (a typed error, a bare prose string under the same key, FastAPI's
 * root-level `detail` array on a 422, and a plain-text 500), which is why this app
 * had no error handling worth the name: `isError` appears in two components and
 * `err.data.error_code` was read in exactly one place.
 *
 * These helpers are that one place, generalised. Read errors through them rather
 * than casting at the call site, so a future shape change has somewhere to land.
 */

/** The error object the server nests under `data`. */
export interface ApiErrorPayload {
  error_code: string;
  message?: string;
  /** Present on `validation_error`: one entry per rejected field. */
  validations?: Record<string, { error_code: string; message: string }>;
}

interface ApiErrorEnvelope {
  data?: ApiErrorPayload;
}

function payloadOf(error: unknown): ApiErrorPayload | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const data = (error as ApiErrorEnvelope).data;
  if (typeof data !== 'object' || data === null) return undefined;
  if (typeof data.error_code !== 'string') return undefined;
  return data;
}

/**
 * The machine-readable code, or `undefined` when this is not an API error at all
 * — a network failure, an abort, a bug in our own code. Those are real and are
 * deliberately not given a fake code: "the request never happened" and "the
 * server refused it" want different handling.
 */
export function errorCode(error: unknown): string | undefined {
  return payloadOf(error)?.error_code;
}

/** True when `error` is the API refusing for the given reason. */
export function isErrorCode(error: unknown, code: string): boolean {
  return errorCode(error) === code;
}

/**
 * Per-field messages from a `validation_error`, ready to hang under inputs.
 * Empty for any other failure, so a form can render it unconditionally.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  const validations = payloadOf(error)?.validations;
  if (!validations) return {};
  return Object.fromEntries(
    Object.entries(validations).map(([field, detail]) => [
      field,
      detail.message,
    ]),
  );
}

/**
 * Something to show a person.
 *
 * Prefers the server's `message` when it sent one — those are the prose details
 * the 7.10 normaliser preserves rather than discards — and otherwise falls back
 * to `fallback`. The `error_code` is never shown: it is for branching, not for
 * reading.
 */
export function errorMessage(error: unknown, fallback: string): string {
  return payloadOf(error)?.message?.trim() || fallback;
}
